import { DOCUMENT, inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { ADPResourceService } from 'digital-core';
import type { SitefinityMenuItem } from 'digital-core';
import { firstValueFrom, from } from 'rxjs';
import { switchMap, timeout } from 'rxjs/operators';
import { PGHAppLoadService } from 'src/app/core/core';

const FOOTER_MENU_KEY = 'Footer_Menu';
const LOAD_TIMEOUT_MS = 10000;
const ALLOWED_PROTOCOLS = ['http:', 'https:'];

/**
 * Legacy-URL redirect to a CMS-driven footer link (e.g. Privacy Policy, Terms of Service). Those
 * links are external and vary per client, so a string redirectTo can't reach them — this sends
 * the browser to the first Footer_Menu item whose Caption or LinkUrl matches `pattern`.
 *
 * - Guards run before any resolver, so on a cold load the CMS isn't loaded yet; we drive the
 *   site-resources load ourselves (same pattern as ServiceRequestActivateGuard), skipped when the
 *   footer menu is already in memory.
 * - Uses location.replace so the legacy URL isn't left in history (Back would re-trigger this
 *   guard and bounce the user forward again).
 * - Falls back to the app root on a load error/timeout, no matching item, a non-http(s) link, or
 *   a link that points back at the URL being redirected (would loop).
 */
export const footerLinkRedirectGuard = (pattern: RegExp): CanActivateFn => async (_route, state): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const document = inject(DOCUMENT);
  const appLoadService = inject(PGHAppLoadService);
  const resourceService = inject(ADPResourceService);

  try {
    // Not existsMenus(): it dereferences siteMenus, which is undefined until the CMS has loaded.
    if (!resourceService.siteMenus?.[FOOTER_MENU_KEY]) {
      await firstValueFrom(
        from(window['__bootstrapConfigPromise'] ?? Promise.resolve()).pipe(
          switchMap(() => appLoadService.loadSiteResources()),
          timeout(LOAD_TIMEOUT_MS)
        )
      );
    }
    const footerMenu = await firstValueFrom(resourceService.getPortalMenus(FOOTER_MENU_KEY).pipe(timeout(LOAD_TIMEOUT_MS)));
    const linkUrl = footerMenu?.MenuItems?.find(
      (item: SitefinityMenuItem) => pattern.test(item.Caption ?? '') || pattern.test(item.LinkUrl ?? '')
    )?.LinkUrl;

    const target = linkUrl ? new URL(linkUrl, document.location.origin) : null;
    if (target && ALLOWED_PROTOCOLS.includes(target.protocol) && !isSameAppUrl(target, document.location.origin, state.url)) {
      document.location.replace(target.href);
      return false;
    }
  } catch {
    // Load failure, timeout, or malformed LinkUrl — fall through to the root redirect below.
  }

  return router.createUrlTree(['/']);
};

function isSameAppUrl(target: URL, origin: string, routerUrl: string): boolean {
  return target.origin === origin && target.pathname === routerUrl.split(/[?#]/)[0];
}

export const PrivacyPolicyRedirectGuard = footerLinkRedirectGuard(/privacy/i);
export const TermsOfUseRedirectGuard = footerLinkRedirectGuard(/terms/i);
